import {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import Combobox from "@/components/Combobox";
import FormHeader from "@/components/FormHeader";
import Modal from "@/components/Modal";
import SkeletonTable from "@/components/SkeletonTable";
import { Badge } from "@/components/ui/badge";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@/components/ui/drawer";
import { Label } from "@/components/ui/label";

import { useDebounce } from "@/hooks/useDebounce";
import { useTahun } from "@/hooks/useTahun";
import api from "@/lib/axios";
import { formatDateIndonesia, formatRupiah } from "@/lib/utils";
import {
  getListJalurJenis,
  getListJenisUkt,
  getListPeriode,
  getListUnitkerja,
} from "@/services/getData";
import {
  DataTagihanUktPlaza,
  DataUktPendaftarLulus,
  JalurJenis,
  JenisUkt,
  PaginationData,
  Periode,
  Unitkerja,
} from "@/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { Loader2 } from "lucide-react";
import toast from "react-hot-toast";
import { LuListFilter } from "react-icons/lu";
import { MdSync } from "react-icons/md";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import FormFilter from "./FormFilter";
import Form from "./FormUKT";
import { Pagination } from "./Pagination";

interface DataTableProps<TData extends DataUktPendaftarLulus, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  paginationData: PaginationData<DataUktPendaftarLulus>;
  isPending: boolean;
}

const postDataSyncTagihanBeasiswa = async ({
  id_sys_periode,
}: {
  id_sys_periode: string;
}) => {
  const response = await api.post("/penerimaan/store/tagihan-beasiswa-ukt", {
    id_sys_periode,
  });
  return response.data;
};

const postDataSyncTagihanInternational = async ({
  id_sys_periode,
}: {
  id_sys_periode: string;
}) => {
  const response = await api.post("/penerimaan/store/tagihan-internasional", {
    id_sys_periode,
  });
  return response.data;
};

const postDataPenetapanUkt = async ({
  id_sys_periode,
}: {
  id_sys_periode: string;
}) => {
  const response = await api.post("/penerimaan/sync/data-ukt-lulus", {
    id_sys_periode,
  });
  return response.data;
};

const postDataPenetapanUktInternational = async ({
  id_sys_periode,
}: {
  id_sys_periode: string;
}) => {
  const response = await api.post("/penerimaan/sync/ukt-lulus-internasional", {
    id_sys_periode,
  });
  return response.data;
};

const postDataPenetapanUktRpl = async ({
  id_sys_periode,
  id_ref_jenis_ukt,
}: {
  id_sys_periode: string;
  id_ref_jenis_ukt: string;
}) => {
  const response = await api.post("/penerimaan/sync/ukt-lulus-rpl", {
    id_sys_periode,
    id_ref_jenis_ukt,
  });
  return response.data;
};

const postDataSyncPembayaran = async ({
  id_sys_periode,
}: {
  id_sys_periode: string;
}) => {
  const response = await api.post(
    "/data-ukt-pendaftar-lulus/sync-data-pembayaran",
    {
      id_sys_periode,
    },
  );
  return response.data;
};

const postDataGenerateNimInternational = async ({
  id_sys_periode,
}: {
  id_sys_periode: string;
}) => {
  const response = await api.post("/penerimaan/generate-nim/internasional", {
    id_sys_periode,
  });
  return response.data;
};

const postDataGenerateNimAfirmasi = async ({
  id_sys_periode,
}: {
  id_sys_periode: string;
}) => {
  const response = await api.post("/penerimaan/generate-nim/afirmasi", {
    id_sys_periode,
  });
  return response.data;
};

export function DataTable<TData extends DataUktPendaftarLulus, TValue>({
  columns,
  data,
  paginationData,
  isPending: isPendingDataPendaftaran,
}: DataTableProps<TData, TValue>) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = useState({});

  const [searchParams] = useSearchParams();
  const ids_ref_jalur_jenis = searchParams.get("ids_ref_jalur_jenis");
  const ids_uk = searchParams.get("ids_uk");
  const { selectedTahun } = useTahun();

  const [form, setForm] = useState({
    id_sys_periode: "",
    tahun: `${new Date().getFullYear()}` || "",
    id_ref_jenis_ukt: "",
  });

  const [selected, setSelected] = useState<DataUktPendaftarLulus | null>(null);
  const [showMenuSync, setShowMenuSync] = useState(false);
  const [showInsert, setShowInsert] = useState(false);
  const [showSyncTagihan, setShowSyncTagihan] = useState(false);
  const [showSyncTagihanInternational, setShowSyncTagihanInternational] =
    useState(false);
  const [showSyncUkt, setShowSyncUkt] = useState(false);
  const [showSyncUktInternational, setShowSyncUktInternational] =
    useState(false);
  const [showSyncUktRpl, setShowSyncUktRpl] = useState(false);
  const [showSyncPembayaran, setShowSyncPembayaran] = useState(false);
  const [showGenerateNimInternational, setShowGenerateNimInternational] =
    useState(false);
  const [showGenerateNimAfirmasi, setShowGenerateNimAfirmasi] = useState(false);
  const [openJenisUkt, setOpenJenisUkt] = useState(false);
  const [openPeriodeSyncTagihan, setOpenPeriodeSyncTagihan] = useState(false);
  const [
    openPeriodeSyncTagihanInternational,
    setOpenPeriodeSyncTagihanInternational,
  ] = useState(false);
  const [openPeriodeSyncUkt, setOpenPeriodeSyncUkt] = useState(false);
  const [openPeriodeSyncUktInternational, setOpenPeriodeSyncUktInternational] =
    useState(false);
  const [openPeriodeSyncUktRpl, setOpenPeriodeSyncUktRpl] = useState(false);
  const [openPeriodeSyncPembayaran, setOpenPeriodeSyncPembayaran] =
    useState(false);
  const [
    openPeriodeGenerateNimInternational,
    setOpenPeriodeGenerateNimInternational,
  ] = useState(false);
  const [openPeriodeGenerateNimAfirmasi, setOpenPeriodeGenerateNimAfirmasi] =
    useState(false);
  const [showFilter, setShowFilter] = useState(false);
  const [searchQuery, setSearchQuery] = useState(
    searchParams.get("search") || "",
  );

  const debouncedQuery = useDebounce(searchQuery, 1000);
  const navigate = useNavigate();
  const location = useLocation();

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    onColumnFiltersChange: setColumnFilters,
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
    },
  });

  const { data: listJalurJenis } = useQuery({
    queryKey: ["listJalurJenis"],
    queryFn: getListJalurJenis,
  });
  const ids_ref_jalur_jenis_array = ids_ref_jalur_jenis?.split("-");
  const listJalurJenisAktifFilter =
    listJalurJenis?.filter((item: JalurJenis) => {
      return ids_ref_jalur_jenis_array?.includes(
        String(item.id_ref_jalur_jenis),
      );
    }) || [];

  const { data: listUnitkerja } = useQuery({
    queryKey: ["listUnitkerja"],
    queryFn: getListUnitkerja,
  });
  const listProdi = listUnitkerja?.filter((unitkerja: Unitkerja) => {
    return unitkerja.unitkerja_group_key == "5" && unitkerja.is_active;
  });
  const ids_uk_array = ids_uk?.split("-");
  const listProdiAktifFilter =
    listProdi?.filter((item: Unitkerja) => {
      return ids_uk_array?.includes(String(item.uk_id));
    }) || [];

  const { data: listJenisUkt, isPending: isPendingListJenisUkt } = useQuery({
    queryKey: ["listJenisUkt"],
    queryFn: getListJenisUkt,
    select: (data) =>
      data?.map((jenisUkt: JenisUkt) => {
        return {
          label: jenisUkt.nama_jenis_ukt,
          value: `${jenisUkt.id_ref_jenis_ukt}`,
        };
      }),
  });

  const { data: dataPeriode, isPending: isPendingDataPeriode } = useQuery({
    queryKey: ["listPeriode", selectedTahun?.value],
    queryFn: () => getListPeriode(String(selectedTahun?.value)),
  });
  const dataPeriodeInternational = dataPeriode?.filter((periode: Periode) => {
    return (
      periode?.jalur_jenis?.nama_jalur_jenis_slug == "internasional" ||
      periode?.jalur_jenis?.nama_jalur_jenis_slug == "afirmasi"
    );
  });
  const dataPeriodeRpl = dataPeriode?.filter((periode: Periode) => {
    return periode?.jalur_jenis?.nama_jalur_jenis_slug == "rpl";
  });

  const dataPeriodeAfirmasi = dataPeriode?.filter((periode: Periode) => {
    return periode?.jalur_jenis?.nama_jalur_jenis_slug == "afirmasi";
  });

  const optionsPeriode =
    dataPeriode?.map((periode: Periode) => ({
      label: `${periode.tahun} - ${periode.jalur_jenis?.nama_jalur_jenis} - Gelombang ${periode.gelombang}`,
      value: `${periode.id_sys_periode}`,
    })) || [];

  const optionsPeriodeInternational =
    dataPeriodeInternational?.map((periode: Periode) => ({
      label: `${periode.tahun} - ${periode.jalur_jenis?.nama_jalur_jenis} - Gelombang ${periode.gelombang}`,
      value: `${periode.id_sys_periode}`,
    })) || [];

  const optionsPeriodeRpl =
    dataPeriodeRpl?.map((periode: Periode) => ({
      label: `${periode.tahun} - ${periode.jalur_jenis?.nama_jalur_jenis} - Gelombang ${periode.gelombang}`,
      value: `${periode.id_sys_periode}`,
    })) || [];

  const optionsPeriodeAfirmasi =
    dataPeriodeAfirmasi?.map((periode: Periode) => ({
      label: `${periode.tahun} - ${periode.jalur_jenis?.nama_jalur_jenis} - Gelombang ${periode.gelombang}`,
      value: `${periode.id_sys_periode}`,
    })) || [];

  const queryClient = useQueryClient();
  const { mutate: mutateSyncTagihan, isPending: isPendingSyncTagihan } =
    useMutation({
      mutationFn: postDataSyncTagihanBeasiswa,
      onSuccess: (data) => {
        queryClient.invalidateQueries({
          queryKey: ["listDataUktPendaftarLulus"],
        });
        setShowSyncTagihan(false);
        toast.success(data?.message || "Success.");
      },
      onError: (error: AxiosError) => {
        const err = error as AxiosError<{ message?: string }>;
        toast.error(
          err?.response?.data?.message || "An unexpected error occurred.",
        );
      },
    });

  const {
    mutate: mutateSyncTagihanInternational,
    isPending: isPendingSyncTagihanInternational,
  } = useMutation({
    mutationFn: postDataSyncTagihanInternational,
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ["listDataUktPendaftarLulus"],
      });
      setShowSyncTagihan(false);
      toast.success(data?.message || "Success.");
    },
    onError: (error: AxiosError) => {
      const err = error as AxiosError<{ message?: string }>;
      toast.error(
        err?.response?.data?.message || "An unexpected error occurred.",
      );
    },
  });

  const { mutate: mutateSyncUkt, isPending: isPendingSyncUkt } = useMutation({
    mutationFn: postDataPenetapanUkt,
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ["listDataUktPendaftarLulus"],
      });
      setShowSyncUkt(false);
      toast.success(data?.message || "Success.");
    },
    onError: (error: AxiosError) => {
      const err = error as AxiosError<{ message?: string }>;
      toast.error(
        err?.response?.data?.message || "An unexpected error occurred.",
      );
    },
  });

  const {
    mutate: mutateSyncUktInternational,
    isPending: isPendingSyncUktInternational,
  } = useMutation({
    mutationFn: postDataPenetapanUktInternational,
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ["listDataUktPendaftarLulus"],
      });
      setShowSyncUktInternational(false);
      toast.success(data?.message || "Success.");
    },
    onError: (error: AxiosError) => {
      const err = error as AxiosError<{ message?: string }>;
      toast.error(
        err?.response?.data?.message || "An unexpected error occurred.",
      );
    },
  });

  const { mutate: mutateSyncUktRpl, isPending: isPendingSyncUktRpl } =
    useMutation({
      mutationFn: postDataPenetapanUktRpl,
      onSuccess: (data) => {
        queryClient.invalidateQueries({
          queryKey: ["listDataUktPendaftarLulus"],
        });
        setShowSyncUktRpl(false);
        toast.success(data?.message || "Success.");
      },
      onError: (error: AxiosError) => {
        const err = error as AxiosError<{ message?: string }>;
        toast.error(
          err?.response?.data?.message || "An unexpected error occurred.",
        );
      },
    });

  const { mutate: mutateSyncPembayaran, isPending: isPendingSyncPembayaran } =
    useMutation({
      mutationFn: postDataSyncPembayaran,
      onSuccess: (data) => {
        queryClient.invalidateQueries({
          queryKey: ["listDataUktPendaftarLulus"],
        });
        setShowSyncPembayaran(false);
        toast.success(data?.message || "Success.");
      },
      onError: (error: AxiosError) => {
        const err = error as AxiosError<{ message?: string }>;
        toast.error(
          err?.response?.data?.message || "An unexpected error occurred.",
        );
      },
    });

  const {
    mutate: mutateGenerateNimInternational,
    isPending: isPendingGenerateNimInternational,
  } = useMutation({
    mutationFn: postDataGenerateNimInternational,
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ["listDataUktPendaftarLulus"],
      });
      setShowGenerateNimInternational(false);
      toast.success(data?.message || "Success.");
    },
    onError: (error: AxiosError) => {
      const err = error as AxiosError<{ message?: string }>;
      toast.error(
        err?.response?.data?.message || "An unexpected error occurred.",
      );
    },
  });

  const {
    mutate: mutateGenerateNimAfirmasi,
    isPending: isPendingGenerateNimAfirmasi,
  } = useMutation({
    mutationFn: postDataGenerateNimAfirmasi,
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ["listDataUktPendaftarLulus"],
      });
      setShowGenerateNimAfirmasi(false);
      toast.success(data?.message || "Success.");
    },
    onError: (error: AxiosError) => {
      const err = error as AxiosError<{ message?: string }>;
      toast.error(
        err?.response?.data?.message || "An unexpected error occurred.",
      );
    },
  });

  const handleSyncDataTagihan = async (
    event: React.FormEvent<HTMLFormElement>,
  ) => {
    event.preventDefault();
    if (!form.id_sys_periode) {
      toast.error("Pilih periode terlebih dahulu");
      return;
    }
    mutateSyncTagihan({
      id_sys_periode: form.id_sys_periode,
    });
  };

  const handleSyncDataTagihanInternational = async (
    event: React.FormEvent<HTMLFormElement>,
  ) => {
    event.preventDefault();
    if (!form.id_sys_periode) {
      toast.error("Pilih periode terlebih dahulu");
      return;
    }
    mutateSyncTagihanInternational({
      id_sys_periode: form.id_sys_periode,
    });
  };

  const handleSyncDataPenetapanUkt = async (
    event: React.FormEvent<HTMLFormElement>,
  ) => {
    event.preventDefault();
    if (!form.id_sys_periode) {
      toast.error("Pilih periode terlebih dahulu");
      return;
    }
    mutateSyncUkt({
      id_sys_periode: form.id_sys_periode,
    });
  };

  const handleSyncDataPenetapanUktInternational = async (
    event: React.FormEvent<HTMLFormElement>,
  ) => {
    event.preventDefault();
    if (!form.id_sys_periode) {
      toast.error("Pilih periode terlebih dahulu");
      return;
    }
    mutateSyncUktInternational({
      id_sys_periode: form.id_sys_periode,
    });
  };

  const handleSyncDataPenetapanUktRpl = async (
    event: React.FormEvent<HTMLFormElement>,
  ) => {
    event.preventDefault();
    if (!form.id_sys_periode) {
      toast.error("Pilih periode terlebih dahulu");
      return;
    }
    if (!form.id_ref_jenis_ukt) {
      toast.error("Pilih jenis UKT terlebih dahulu");
      return;
    }
    mutateSyncUktRpl({
      id_sys_periode: form.id_sys_periode,
      id_ref_jenis_ukt: form.id_ref_jenis_ukt,
    });
  };

  const handleSyncPembayaran = async (
    event: React.FormEvent<HTMLFormElement>,
  ) => {
    event.preventDefault();
    if (!form.id_sys_periode) {
      toast.error("Pilih periode terlebih dahulu");
      return;
    }
    mutateSyncPembayaran({ id_sys_periode: form.id_sys_periode });
  };

  const handleGenerateNimInternational = async (
    event: React.FormEvent<HTMLFormElement>,
  ) => {
    event.preventDefault();
    if (!form.id_sys_periode) {
      toast.error("Pilih periode terlebih dahulu");
      return;
    }
    mutateGenerateNimInternational({ id_sys_periode: form.id_sys_periode });
  };
  const handleGenerateNimAfirmasi = async (
    event: React.FormEvent<HTMLFormElement>,
  ) => {
    event.preventDefault();
    if (!form.id_sys_periode) {
      toast.error("Pilih periode terlebih dahulu");
      return;
    }
    mutateGenerateNimAfirmasi({ id_sys_periode: form.id_sys_periode });
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);

    if (debouncedQuery) {
      if (params.get("search") !== debouncedQuery) {
        params.delete("page");
      }

      if (
        params.get("search") !== debouncedQuery ||
        params.get("page") !== "1"
      ) {
        params.set("search", debouncedQuery);
        navigate(`${location.pathname}?${params.toString()}`, {
          replace: true,
        });
      }
    } else {
      if (params.has("search") || params.get("page") !== "1") {
        params.delete("search");
        navigate(`${location.pathname}?${params.toString()}`, {
          replace: true,
        });
      }
    }
  }, [debouncedQuery, navigate, location.pathname, location.search]);

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 py-4">
        <div className="flex-1 flex items-center gap-3">
          <Input
            autoFocus
            placeholder="Filter by nomor pendaftaran, nama lengkap..."
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            className="max-w-sm"
          />
          <Button
            variant={ids_ref_jalur_jenis ? "default" : "outline"}
            className="flex items-center gap-x-3"
            onClick={() => setShowFilter(true)}
          >
            Filter
            <LuListFilter />
          </Button>

          <div className="flex gap-1 flex-wrap">
            {listJalurJenisAktifFilter?.map((item: JalurJenis) => (
              <Badge variant={"outline"}>{item?.nama_jalur_jenis}</Badge>
            ))}
            {listProdiAktifFilter?.map((item: Unitkerja) => (
              <Badge variant={"outline"}>{item?.nama}</Badge>
            ))}
          </div>
        </div>

        <div className="flex flex-wrap gap-3">
          <Button
            variant={"outline"}
            className="flex items-center gap-x-3"
            onClick={() => setShowMenuSync(true)}
          >
            Sinkronisasi
            <MdSync />
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="ml-auto">
                Columns
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {table
                .getAllColumns()
                .filter((column) => column.getCanHide())
                .map((column) => {
                  return (
                    <DropdownMenuCheckboxItem
                      key={column.id}
                      className="capitalize"
                      checked={column.getIsVisible()}
                      onCheckedChange={(value) =>
                        column.toggleVisibility(!!value)
                      }
                    >
                      {column.id}
                    </DropdownMenuCheckboxItem>
                  );
                })}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      {isPendingDataPendaftaran ? (
        <div className="flex flex-col gap-y-3">
          <SkeletonTable />
        </div>
      ) : (
        <div className="border rounded-xl">
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => {
                return (
                  <TableRow key={headerGroup.id}>
                    {headerGroup.headers.map((header, index) => {
                      return (
                        <>
                          <TableHead key={header.id}>
                            {header.isPlaceholder
                              ? null
                              : flexRender(
                                  header.column.columnDef.header,
                                  header.getContext(),
                                )}
                          </TableHead>
                          {index === 0 && <TableHead></TableHead>}
                        </>
                      );
                    })}
                  </TableRow>
                );
              })}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow
                    key={row.id}
                    data-state={row.getIsSelected() && "selected"}
                  >
                    {row.getVisibleCells().map((cell, index) => (
                      <>
                        <TableCell key={cell.id}>
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </TableCell>

                        {index === 0 && (
                          <TableCell>
                            <Drawer>
                              <DrawerTrigger
                                onClick={() => setSelected(row.original)}
                              >
                                Lihat
                              </DrawerTrigger>
                              <DrawerContent className="h-[90vh]">
                                <DrawerHeader>
                                  <DrawerTitle>Data Pendaftaran</DrawerTitle>

                                  <DrawerDescription>
                                    {
                                      selected?.data_pendaftaran?.data_siswa
                                        ?.nama_lengkap
                                    }
                                  </DrawerDescription>
                                  <div className="mt-5">
                                    {selected?.data_tagihan_ukt_plaza?.map(
                                      (
                                        tagihan: DataTagihanUktPlaza,
                                        index: number,
                                      ) => {
                                        return (
                                          <div
                                            key={
                                              tagihan.id_data_tagihan_ukt_plaza
                                            }
                                            className="p-5 border rounded-xl"
                                          >
                                            <div className="mb-3 flex items-center gap-3">
                                              <span className="text-2xl font-bold">
                                                Tagihan{" "}
                                              </span>
                                              <div className="h-7 w-7 border rounded-full flex justify-center items-center">
                                                {index + 1}
                                              </div>
                                            </div>
                                            <table>
                                              <tbody>
                                                <tr>
                                                  <th className="py-2 px-3">
                                                    Customer No
                                                  </th>
                                                  <td className="py-2 px-3">
                                                    :
                                                  </td>
                                                  <td className="py-2 px-3">
                                                    {tagihan.customer_no}
                                                  </td>
                                                </tr>
                                                <tr>
                                                  <th className="py-2 px-3">
                                                    Nomor Tagihan
                                                  </th>
                                                  <td className="py-2 px-3">
                                                    :
                                                  </td>
                                                  <td className="py-2 px-3">
                                                    {tagihan.nomor_tagihan}
                                                  </td>
                                                </tr>
                                                <tr>
                                                  <th className="py-2 px-3">
                                                    Amount
                                                  </th>
                                                  <td className="py-2 px-3">
                                                    :
                                                  </td>
                                                  <td className="py-2 px-3">
                                                    {tagihan.amount &&
                                                      formatRupiah(
                                                        Number(tagihan.amount),
                                                      )}
                                                  </td>
                                                </tr>
                                                <tr>
                                                  <th className="py-2 px-3">
                                                    Keterangan
                                                  </th>
                                                  <td className="py-2 px-3">
                                                    :
                                                  </td>
                                                  <td className="py-2 px-3">
                                                    {tagihan.keterangan}
                                                  </td>
                                                </tr>
                                                <tr>
                                                  <th className="py-2 px-3">
                                                    Expired Date
                                                  </th>
                                                  <td className="py-2 px-3">
                                                    :
                                                  </td>
                                                  <td className="py-2 px-3">
                                                    {tagihan.expired_date &&
                                                      formatDateIndonesia(
                                                        tagihan.expired_date,
                                                        "full",
                                                      )}
                                                  </td>
                                                </tr>
                                                <tr>
                                                  <th className="py-2 px-3">
                                                    Status Pembayaran
                                                  </th>
                                                  <td className="py-2 px-3">
                                                    :
                                                  </td>
                                                  <td className="py-2 px-3">
                                                    {tagihan.status_bayar ===
                                                      1 && (
                                                      <Badge
                                                        variant={"secondary"}
                                                      >
                                                        Belum dibayar
                                                      </Badge>
                                                    )}
                                                    {tagihan.status_bayar ===
                                                      2 && (
                                                      <Badge
                                                        variant={"default"}
                                                      >
                                                        Sudah dibayar
                                                      </Badge>
                                                    )}
                                                    {tagihan.status_bayar ===
                                                      3 && (
                                                      <Badge
                                                        variant={"destructive"}
                                                      >
                                                        Expired
                                                      </Badge>
                                                    )}
                                                    {tagihan.status_bayar ===
                                                      4 && (
                                                      <Badge
                                                        variant={"default"}
                                                      >
                                                        Sudah dibayar (tunda)
                                                      </Badge>
                                                    )}
                                                    {tagihan.status_bayar ===
                                                      5 && (
                                                      <Badge
                                                        variant={"default"}
                                                      >
                                                        Beasiswa
                                                      </Badge>
                                                    )}
                                                  </td>
                                                </tr>
                                              </tbody>
                                            </table>
                                          </div>
                                        );
                                      },
                                    )}
                                  </div>
                                </DrawerHeader>
                                <DrawerFooter>
                                  {/* <Button>Submit</Button> */}
                                  <DrawerClose className="w-full">
                                    <Button
                                      variant="default"
                                      className="w-full"
                                    >
                                      Tutup
                                    </Button>
                                  </DrawerClose>
                                </DrawerFooter>
                              </DrawerContent>
                            </Drawer>
                          </TableCell>
                        )}
                      </>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center"
                  >
                    No results.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      )}

      <br />
      <div>
        <Pagination table={table} paginationData={paginationData} />
      </div>

      <Modal show={showInsert} onClose={() => setShowInsert(false)}>
        <Form closeModal={() => setShowInsert(false)} method="POST" />
      </Modal>

      <Modal
        show={showSyncTagihan}
        onClose={() => setShowSyncTagihan(false)}
        closeable={!isPendingSyncTagihan}
      >
        <form
          onSubmit={handleSyncDataTagihan}
          className="flex flex-col gap-y-3"
        >
          <FormHeader
            icon={<MdSync />}
            title="Sinkronisasi Data Tagihan Beasiswa"
            description={`Apakah anda yakin akan melakukan sinkronisasi data?`}
          />
          <div>
            <Label>Periode</Label>
            <Combobox
              isPending={isPendingDataPeriode}
              open={openPeriodeSyncTagihan}
              setOpen={setOpenPeriodeSyncTagihan}
              value={form.id_sys_periode}
              setValue={(value) => {
                setForm((prev) => ({ ...prev, id_sys_periode: value }));
              }}
              options={optionsPeriode}
              placeholder="Pilih Periode"
            />
          </div>
          <div className="mt-3 flex gap-3">
            <Button
              type="button"
              variant={"outline"}
              disabled={isPendingSyncTagihan}
              onClick={() => setShowSyncTagihan(false)}
            >
              Batal
            </Button>
            <Button
              variant={"default"}
              className="flex items-center gap-3"
              disabled={isPendingSyncTagihan}
            >
              {isPendingSyncTagihan ? (
                <>
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  Processing...
                </>
              ) : (
                "Sinkron"
              )}
            </Button>
          </div>
        </form>
      </Modal>

      <Modal
        show={showSyncTagihanInternational}
        onClose={() => setShowSyncTagihanInternational(false)}
        closeable={!isPendingSyncTagihanInternational}
      >
        <form
          onSubmit={handleSyncDataTagihanInternational}
          className="flex flex-col gap-y-3"
        >
          <FormHeader
            icon={<MdSync />}
            title="Sinkronisasi Data Tagihan International / Afirmasi"
            description={`Apakah anda yakin akan melakukan sinkronisasi data?`}
          />
          <div>
            <Label>Periode</Label>
            <Combobox
              isPending={isPendingDataPeriode}
              open={openPeriodeSyncTagihanInternational}
              setOpen={setOpenPeriodeSyncTagihanInternational}
              value={form.id_sys_periode}
              setValue={(value) => {
                setForm((prev) => ({ ...prev, id_sys_periode: value }));
              }}
              options={optionsPeriodeInternational}
              placeholder="Pilih Periode"
            />
          </div>
          <div className="mt-3 flex gap-3">
            <Button
              type="button"
              variant={"outline"}
              disabled={isPendingSyncTagihanInternational}
              onClick={() => setShowSyncTagihanInternational(false)}
            >
              Batal
            </Button>
            <Button
              variant={"default"}
              className="flex items-center gap-3"
              disabled={isPendingSyncTagihanInternational}
            >
              {isPendingSyncTagihanInternational ? (
                <>
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  Processing...
                </>
              ) : (
                "Sinkron"
              )}
            </Button>
          </div>
        </form>
      </Modal>

      <Modal
        show={showSyncUkt}
        onClose={() => setShowSyncUkt(false)}
        closeable={!isPendingSyncUkt}
      >
        <form
          onSubmit={handleSyncDataPenetapanUkt}
          className="flex flex-col gap-y-3"
        >
          <FormHeader
            icon={<MdSync />}
            title="Sinkronisasi Data Penetapan UKT pada Sistem Seleksi"
            description={`Apakah anda yakin akan melakukan sinkronisasi data?`}
          />
          <div>
            <Label>Periode</Label>
            <Combobox
              isPending={isPendingDataPeriode}
              open={openPeriodeSyncUkt}
              setOpen={setOpenPeriodeSyncUkt}
              value={form.id_sys_periode}
              setValue={(value) => {
                setForm((prev) => ({ ...prev, id_sys_periode: value }));
              }}
              options={optionsPeriode}
              placeholder="Pilih Periode"
            />
          </div>
          <div className="mt-3 flex gap-3">
            <Button
              type="button"
              variant={"outline"}
              disabled={isPendingSyncUkt}
              onClick={() => setShowSyncUkt(false)}
            >
              Batal
            </Button>
            <Button
              variant={"default"}
              className="flex items-center gap-3"
              disabled={isPendingSyncUkt}
            >
              {isPendingSyncUkt ? (
                <>
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  Processing...
                </>
              ) : (
                "Sinkron"
              )}
            </Button>
          </div>
        </form>
      </Modal>

      <Modal
        show={showSyncUktInternational}
        onClose={() => setShowSyncUktInternational(false)}
        closeable={!isPendingSyncUktInternational}
      >
        <form
          onSubmit={handleSyncDataPenetapanUktInternational}
          className="flex flex-col gap-y-3"
        >
          <FormHeader
            icon={<MdSync />}
            title="Sinkronisasi Data Penetapan UKT Program International / Afirmasi"
            description={`Apakah anda yakin akan melakukan sinkronisasi data?`}
          />
          <div>
            <Label>Periode</Label>
            <Combobox
              isPending={isPendingDataPeriode}
              open={openPeriodeSyncUktInternational}
              setOpen={setOpenPeriodeSyncUktInternational}
              value={form.id_sys_periode}
              setValue={(value) => {
                setForm((prev) => ({ ...prev, id_sys_periode: value }));
              }}
              options={optionsPeriodeInternational}
              placeholder="Pilih Periode"
            />
          </div>
          <div className="mt-3 flex gap-3">
            <Button
              type="button"
              variant={"outline"}
              disabled={isPendingSyncUktInternational}
              onClick={() => setShowSyncUktInternational(false)}
            >
              Batal
            </Button>
            <Button
              variant={"default"}
              className="flex items-center gap-3"
              disabled={isPendingSyncUktInternational}
            >
              {isPendingSyncUktInternational ? (
                <>
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  Processing...
                </>
              ) : (
                "Sinkron"
              )}
            </Button>
          </div>
        </form>
      </Modal>

      <Modal
        show={showSyncUktRpl}
        onClose={() => setShowSyncUktRpl(false)}
        closeable={!isPendingSyncUktRpl}
      >
        <form
          onSubmit={handleSyncDataPenetapanUktRpl}
          className="flex flex-col gap-y-3"
        >
          <FormHeader
            icon={<MdSync />}
            title="Sinkronisasi Data Penetapan UKT RPL"
            description={`Apakah anda yakin akan melakukan sinkronisasi data?`}
          />
          <div>
            <Label>Periode</Label>
            <Combobox
              isPending={isPendingDataPeriode}
              open={openPeriodeSyncUktRpl}
              setOpen={setOpenPeriodeSyncUktRpl}
              value={form.id_sys_periode}
              setValue={(value) => {
                setForm((prev) => ({ ...prev, id_sys_periode: value }));
              }}
              options={optionsPeriodeRpl}
              placeholder="Pilih Periode"
            />
          </div>
          <div>
            <Label>Jenis UKT</Label>
            <Combobox
              isPending={isPendingListJenisUkt}
              open={openJenisUkt}
              setOpen={setOpenJenisUkt}
              value={form.id_ref_jenis_ukt || ""}
              setValue={(value) => {
                setForm((prev) => ({ ...prev, id_ref_jenis_ukt: value }));
              }}
              options={listJenisUkt}
              placeholder="Pilih Jenis UKT"
            />
          </div>
          <div className="mt-3 flex gap-3">
            <Button
              type="button"
              variant={"outline"}
              disabled={isPendingSyncUktRpl}
              onClick={() => setShowSyncUktRpl(false)}
            >
              Batal
            </Button>
            <Button
              variant={"default"}
              className="flex items-center gap-3"
              disabled={isPendingSyncUktRpl}
            >
              {isPendingSyncUktRpl ? (
                <>
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  Processing...
                </>
              ) : (
                "Sinkron"
              )}
            </Button>
          </div>
        </form>
      </Modal>

      <Modal show={showFilter} onClose={() => setShowFilter(false)} width="2xl">
        <FormFilter closeModal={() => setShowFilter(false)} />
      </Modal>

      <Modal
        show={showMenuSync}
        onClose={() => setShowMenuSync(false)}
        className="w-[32vw]"
      >
        <div className="flex flex-col gap-y-3">
          <FormHeader title="Sinkronisasi Data" icon={<MdSync />} />
          <Button
            variant={"outline"}
            className="flex items-center gap-x-3"
            onClick={() => setShowSyncUkt(true)}
          >
            Sinkronisasi Hasil Penetapan UKT
            <MdSync />
          </Button>

          <Button
            variant={"outline"}
            className="flex items-center gap-x-3"
            onClick={() => setShowSyncUktInternational(true)}
          >
            Sinkronisasi Hasil Penetapan UKT International / Afirmasi
            <MdSync />
          </Button>

          <Button
            variant={"outline"}
            className="flex items-center gap-x-3"
            onClick={() => setShowSyncUktRpl(true)}
          >
            Sinkronisasi Hasil Penetapan UKT RPL
            <MdSync />
          </Button>

          <Button
            variant={"outline"}
            className="flex items-center gap-x-3"
            onClick={() => setShowSyncTagihan(true)}
          >
            Sinkronisasi Tagihan Beasiswa
            <MdSync />
          </Button>

          <Button
            variant={"outline"}
            className="flex items-center gap-x-3"
            onClick={() => setShowSyncTagihanInternational(true)}
          >
            Sinkronisasi Tagihan International / Afirmasi
            <MdSync />
          </Button>

          <Button
            variant={"outline"}
            className="flex items-center gap-x-3"
            onClick={() => setShowSyncPembayaran(true)}
          >
            Sinkronisasi Data Pembayaran
            <MdSync />
          </Button>

          <Button
            variant={"outline"}
            className="flex items-center gap-x-3"
            onClick={() => setShowGenerateNimInternational(true)}
          >
            Generate NIM International
            <MdSync />
          </Button>
          <Button
            variant={"outline"}
            className="flex items-center gap-x-3"
            onClick={() => setShowGenerateNimAfirmasi(true)}
          >
            Generate NIM Afirmasi
            <MdSync />
          </Button>
        </div>
      </Modal>

      <Modal
        show={showSyncPembayaran}
        onClose={() => setShowSyncPembayaran(false)}
        closeable={!isPendingSyncPembayaran}
      >
        <form onSubmit={handleSyncPembayaran} className="flex flex-col gap-y-3">
          <FormHeader
            icon={<MdSync />}
            title="Sinkronisasi Data Pembayaran"
            description={`Apakah anda yakin akan melakukan sinkronisasi data?`}
          />
          <div>
            <Label>Periode</Label>
            <Combobox
              isPending={isPendingDataPeriode}
              open={openPeriodeSyncPembayaran}
              setOpen={setOpenPeriodeSyncPembayaran}
              value={form.id_sys_periode}
              setValue={(value) => {
                setForm((prev) => ({ ...prev, id_sys_periode: value }));
              }}
              options={optionsPeriode}
              placeholder="Pilih Periode"
            />
          </div>
          <div className="mt-3 flex gap-3">
            <Button
              type="button"
              variant={"outline"}
              disabled={isPendingSyncPembayaran}
              onClick={() => setShowSyncPembayaran(false)}
            >
              Batal
            </Button>
            <Button
              variant={"default"}
              className="flex items-center gap-3"
              disabled={isPendingSyncPembayaran}
            >
              {isPendingSyncPembayaran ? (
                <>
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  Processing...
                </>
              ) : (
                "Sinkron"
              )}
            </Button>
          </div>
        </form>
      </Modal>

      <Modal
        show={showGenerateNimInternational}
        onClose={() => setShowGenerateNimInternational(false)}
        closeable={!isPendingGenerateNimInternational}
      >
        <form
          onSubmit={handleGenerateNimInternational}
          className="flex flex-col gap-y-3"
        >
          <FormHeader
            icon={<MdSync />}
            title="Generate NIM International"
            description={`Apakah anda yakin akan melakukan generate data?`}
          />
          <div>
            <Label>Periode</Label>
            <Combobox
              isPending={isPendingDataPeriode}
              open={openPeriodeGenerateNimInternational}
              setOpen={setOpenPeriodeGenerateNimInternational}
              value={form.id_sys_periode}
              setValue={(value) => {
                setForm((prev) => ({ ...prev, id_sys_periode: value }));
              }}
              options={optionsPeriodeInternational}
              placeholder="Pilih Periode"
            />
          </div>
          <div className="mt-3 flex gap-3">
            <Button
              type="button"
              variant={"outline"}
              disabled={isPendingGenerateNimInternational}
              onClick={() => setShowGenerateNimInternational(false)}
            >
              Batal
            </Button>
            <Button
              variant={"default"}
              className="flex items-center gap-3"
              disabled={isPendingGenerateNimInternational}
            >
              {isPendingGenerateNimInternational ? (
                <>
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  Processing...
                </>
              ) : (
                "Generate NIM"
              )}
            </Button>
          </div>
        </form>
      </Modal>

      <Modal
        show={showGenerateNimAfirmasi}
        onClose={() => setShowGenerateNimAfirmasi(false)}
        closeable={!isPendingGenerateNimAfirmasi}
      >
        <form
          onSubmit={handleGenerateNimAfirmasi}
          className="flex flex-col gap-y-3"
        >
          <FormHeader
            icon={<MdSync />}
            title="Generate NIM Afirmasi"
            description={`Apakah anda yakin akan melakukan generate data?`}
          />
          <div>
            <Label>Periode</Label>
            <Combobox
              isPending={isPendingDataPeriode}
              open={openPeriodeGenerateNimAfirmasi}
              setOpen={setOpenPeriodeGenerateNimAfirmasi}
              value={form.id_sys_periode}
              setValue={(value) => {
                setForm((prev) => ({ ...prev, id_sys_periode: value }));
              }}
              options={optionsPeriodeAfirmasi}
              placeholder="Pilih Periode"
            />
          </div>
          <div className="mt-3 flex gap-3">
            <Button
              type="button"
              variant={"outline"}
              disabled={isPendingGenerateNimAfirmasi}
              onClick={() => setShowGenerateNimAfirmasi(false)}
            >
              Batal
            </Button>
            <Button
              variant={"default"}
              className="flex items-center gap-3"
              disabled={isPendingGenerateNimAfirmasi}
            >
              {isPendingGenerateNimAfirmasi ? (
                <>
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  Processing...
                </>
              ) : (
                "Generate NIM"
              )}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
